import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { blockUser, closeMeetingSession, fetchPresence, fetchProfile, fetchRecordings, joinRoom, leaveRoom, openMeetingSession, reportLink, reportMedia } from "./api.js";
import { addBeagleFriend, isCarrierAddress, isNativeMeetHost, pickAddOrigin, postToNativeHost } from "./auth.js";
import { RoomClient, unlockAudio } from "./room-client.js";
import { MeetAvatar, VideoTile, identityFromStream, speakerKey, streamHasAudio, streamHasVideo } from "./VideoTile.jsx";
import { splitChatLinks } from "./chat-links.js";
import { isDesktopMac, MAC_EFFECTS_HELP } from "./background-blur.js";
import { canScreenShare, isMobileDevice } from "./device.js";
import { formatClock, formatElapsed, parsePunkId, shortId } from "./format.js";

function ChatText({ text }) {
  return splitChatLinks(text).map((part, i) =>
    part.href ? (
      <a
        key={i}
        className="bm-link"
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {part.text}
      </a>
    ) : (
      <span key={i}>{part.text}</span>
    )
  );
}

function isIOSWebKit() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return /Safari/i.test(ua) && !/Chrome|Chromium|Android|CriOS|FxiOS|Edg/i.test(ua);
}

function isScreen(stream) {
  const a = stream?.getAttributes?.() || {};
  return a.screen === true || a.type === "screen";
}

function Glyph({ kind, off }) {
  const stroke = off ? "oklch(0.72 0.13 25)" : "currentColor";
  if (kind === "effects") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "flip") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 9a8 8 0 0 1 14-3l2 2M20 3v5h-5M20 15a8 8 0 0 1-14 3l-2-2M4 21v-5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "mic") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="9" y="3" width="6" height="11" rx="3" stroke={stroke} strokeWidth="1.8" />
        <path d="M6 11a6 6 0 0 0 12 0" stroke={stroke} strokeWidth="1.8" />
        <path d="M12 17v3M8 21h8" stroke={stroke} strokeWidth="1.8" />
        {off ? <path d="M5 5l14 14" stroke={stroke} strokeWidth="1.8" /> : null}
      </svg>
    );
  }
  if (kind === "cam") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="6" width="13" height="12" rx="2.5" stroke={stroke} strokeWidth="1.8" />
        <circle cx="9.5" cy="12" r="2.2" stroke={stroke} strokeWidth="1.6" />
        <path d="M16 10l5-2.5v9L16 14" stroke={stroke} strokeWidth="1.8" />
        {off ? <path d="M4 5l16 14" stroke={stroke} strokeWidth="1.8" /> : null}
      </svg>
    );
  }
  if (kind === "screen") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 20h8M12 17v3" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="13" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 21l3-3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function streamRank(stream) {
  if (isScreen(stream)) return 3;
  if (streamHasVideo(stream)) return 2;
  return 1;
}

function hasRealName(ident) {
  const n = String(ident?.name || "").trim();
  return n.length > 0 && n.toLowerCase() !== "participant";
}

function firstDeviceLabel(list, fallback) {
  const label = String(list?.[0]?.label || "").trim();
  return label || fallback;
}

function identOf(stream, isLocal, localIdentity) {
  if (isLocal && localIdentity) return localIdentity;
  return identityFromStream(stream, localIdentity, isLocal);
}

function groupSpeakers(local, camera, screen, remote, localUserId, localIdentity) {
  const groups = new Map();
  const add = (stream, isLocal, id) => {
    if (!stream) return;
    const key = speakerKey(stream, isLocal, localUserId);
    let g = groups.get(key);
    if (!g) {
      g = { key, local: isLocal || key === "local", items: [] };
      groups.set(key, g);
    }
    g.items.push({ id, stream, local: isLocal });
  };
  add(local, true, "local-mic");
  add(camera, true, "local-cam");
  add(screen, true, "local-screen");
  remote.forEach((stream, id) => add(stream, false, String(id)));

  const tiles = [];
  for (const g of groups.values()) {
    const person =
      (g.local ? localIdentity : null) ||
      g.items.map((i) => identOf(i.stream, g.local, localIdentity)).find((p) => hasRealName(p) && p.address) ||
      g.items.map((i) => identOf(i.stream, g.local, localIdentity)).find(hasRealName) ||
      identOf(g.items[0].stream, g.local, localIdentity);
    const visuals = g.items.filter((i) => streamRank(i.stream) >= 2);
    const shown = visuals.length ? visuals : g.items.slice(0, 1);
    const audioItem = g.items.find((i) => !isScreen(i.stream) && streamHasAudio(i.stream));
    for (const item of shown) {
      const kind = isScreen(item.stream) ? "screen" : streamHasVideo(item.stream) ? "video" : "audio";
      tiles.push({
        id: `${g.key}:${kind}:${item.id}`,
        speakerId: g.key,
        stream: item.stream,
        local: g.local,
        person,
        kind,
        audioStream: kind === "screen" ? null : audioItem?.stream || null,
        streamIds: [String(item.id), String(item.stream.getID?.() || "")],
      });
    }
  }
  return tiles;
}

export function MeetingRoom({
  token,
  identity,
  roomName,
  roomId,
  erizoSrc,
  apiBase = "",
  inApp = false,
  skipMic = false,
  requireMic = true,
  isOwner = false,
  initialPresence = null,
  onExit,
}) {
  const username = identity.userId || identity.userid;
  const usernameRef = useRef(username);
  usernameRef.current = username;
  const [client] = useState(() => new RoomClient(token, username));
  const savedPresence = client.recordingContext?.roomId === roomId ? client.recordingContext : null;
  client.recordingContext = {
    apiBase,
    roomId,
    roomName,
    meetingSessionId: savedPresence?.meetingSessionId || initialPresence?.meetingSessionId || "",
    participantSessionId: savedPresence?.participantSessionId || initialPresence?.participantSessionId || "",
    connectionId: savedPresence?.connectionId || initialPresence?.connectionId || "",
  };
  const [error, setError] = useState(null);
  const needTap = isIOSWebKit();
  const [loading, setLoading] = useState(true);
  const identityRef = useRef(identity);
  identityRef.current = identity;
  useEffect(() => {
    client.applyIdentity(identity);
  }, [client, identity]);
  const peerProfilesRef = useRef(new Map());
  const [peerTick, setPeerTick] = useState(0);
  const [local, setLocal] = useState(null);
  const [camera, setCamera] = useState(null);
  const [screen, setScreen] = useState(null);
  const [remote, setRemote] = useState(() => new Map());
  const [peerMedia, setPeerMedia] = useState(() => new Map());
  const [muted, setMuted] = useState(!!skipMic);
  const [mics, setMics] = useState([]);
  const [micId, setMicId] = useState("");
  const [micLabel, setMicLabel] = useState("");
  const [micLive, setMicLive] = useState(!skipMic);
  const [micOpen, setMicOpen] = useState(false);
  const [micBusy, setMicBusy] = useState(false);
  const [audioSinks, setAudioSinks] = useState([]);
  const [sinkId, setSinkId] = useState("");
  const [sinkLabel, setSinkLabel] = useState("System default");
  const [canSink] = useState(() => client.canSelectSpeaker());
  const [camOn, setCamOn] = useState(false);
  const [camBusy, setCamBusy] = useState(false);
  const [blur, setBlur] = useState({ controllable: false, known: false, on: null });
  const [effectsOpen, setEffectsOpen] = useState(false);
  const mobileCamera = isMobileDevice(navigator);
  const desktopMac = isDesktopMac(navigator);
  const showEffects = desktopMac || blur.controllable || blur.known;
  const showScreenShare = canScreenShare(navigator);
  const [sharing, setSharing] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [stageId, setStageId] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const chatOpenRef = useRef(false);
  chatOpenRef.current = chatOpen;
  const [chat, setChat] = useState("");
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [toast, setToast] = useState(null);
  const [friendOf, setFriendOf] = useState(null);
  const [friendHint, setFriendHint] = useState("");
  const [friendPos, setFriendPos] = useState(null);
  const [adding, setAdding] = useState(false);
  const railRef = useRef(null);
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  const [reconnecting, setReconnecting] = useState(false);
  const leavingRef = useRef(false);
  const recoveringRef = useRef(false);
  const recoverRef = useRef(async () => {});
  const lastRecoverOkRef = useRef(0);
  const [reconnectAt, setReconnectAt] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const kick = () => {
      unlockAudio();
      client.kickPlayback();
    };
    document.addEventListener("click", kick);
    document.addEventListener("pointerdown", kick);
    return () => {
      document.removeEventListener("click", kick);
      document.removeEventListener("pointerdown", kick);
    };
  }, [client]);

  const noteChat = useCallback((text) => {
    setMessages((prev) => [
      ...prev,
      { who: "Meeting", text, ts: Date.now(), kind: "system" },
    ]);
    if (!chatOpenRef.current) setUnread((n) => n + 1);
  }, []);

  const rosterRef = useRef(new Map());
  const rosterQuietUntil = useRef(0);
  const hushRoster = useCallback(() => {
    rosterQuietUntil.current = Date.now() + 8000;
  }, []);

  const displayPeer = useCallback((stream) => {
    const ident = identityFromStream(stream, null, false);
    if (hasRealName(ident)) return ident.name;
    const id = ident.userId || ident.userid;
    return id ? shortId(id) : "Someone";
  }, []);

  const trackRemote = useCallback(
    (stream, added) => {
      const key = speakerKey(stream, false, usernameRef.current);
      if (!key || key === "local") return;
      const quiet =
        Date.now() < rosterQuietUntil.current ||
        recoveringRef.current ||
        leavingRef.current ||
        !client.connected;
      const map = rosterRef.current;
      if (added) {
        let rec = map.get(key);
        if (!rec) {
          rec = { n: 0, name: displayPeer(stream) };
          map.set(key, rec);
        } else if (hasRealName(identityFromStream(stream, null, false))) {
          rec.name = displayPeer(stream);
        }
        rec.n += 1;
        if (rec.n === 1 && !quiet) noteChat(`${rec.name} joined`);
        return;
      }
      const rec = map.get(key);
      if (!rec) return;
      rec.n = Math.max(0, rec.n - 1);
      if (rec.n === 0) {
        map.delete(key);
        if (!quiet) noteChat(`${rec.name} left`);
      }
    },
    [displayPeer, noteChat, client]
  );

  const recover = useCallback(
    async () => {
      if (leavingRef.current || recoveringRef.current) return;
      recoveringRef.current = true;
      hushRoster();
      client.beginRecover();
      const started = Date.now();
      setReconnectAt(started);
      setReconnecting(true);
      setError(null);
      reportLink(apiBase, roomId, "recover", "mint-token");
      noteChat(`Reconnecting… ${formatClock(started)}`);
      setToast({ who: "Meeting", text: `Reconnecting… ${formatClock(started)}`, kind: "system" });
      let delay = 2000;
      try {
        while (!leavingRef.current) {
          try {
            const data = await joinRoom(apiBase, roomId, "presenter", { reconnect: true });
            client.recordingEnabled = data.recording === true;
            if (leavingRef.current) {
              leaveRoom(apiBase, roomId);
              return;
            }
            const presence = await openMeetingSession(apiBase, roomId, { reconnect: true }).catch(() => null);
            if (presence) {
              client.recordingContext = {
                ...client.recordingContext,
                meetingSessionId: presence.meetingSessionId,
                participantSessionId: presence.participantSessionId,
                connectionId: presence.connectionId,
              };
            }
            await client.reconnect(data.token, {
              erizoSrc,
              identity: identityRef.current,
              skipMic,
              requireMic,
              role: "presenter",
            });
            if (leavingRef.current) return;
            lastRecoverOkRef.current = Date.now();
            setCamOn(!!client._wantCam && !!client.camera);
            setSharing(!!client._wantShare && !!client.screen);
            setMuted(!!client._wantMute);
            noteChat(`Reconnected ${formatClock(lastRecoverOkRef.current)}`);
            setToast({
              who: "Meeting",
              text: `Reconnected ${formatClock(lastRecoverOkRef.current)}`,
              kind: "system",
            });
            setLoading(false);
            return;
          } catch (err) {
            if (leavingRef.current) return;
            const msg = err instanceof Error ? err.message : "Reconnect failed";
            if (msg === "hangup") return;
            if (msg.includes("Sign in")) {
              reportLink(apiBase, roomId, "recover-fail", msg);
              setError(`${msg} ${formatClock()}`);
              setLoading(false);
              return;
            }
            const busy = /502|Nuve|Failed to join|timeout/i.test(msg);
            await new Promise((r) => setTimeout(r, busy ? Math.max(delay, 5000) : delay));
            if (leavingRef.current) return;
            delay = Math.min(delay * 2, busy ? 20000 : 15000);
          }
        }
      } finally {
        recoveringRef.current = false;
        client.endRecover();
        setReconnecting(false);
      }
    },
    [apiBase, roomId, client, erizoSrc, skipMic, requireMic, noteChat, hushRoster]
  );
  recoverRef.current = recover;

  useEffect(() => {
    let alive = true;
    let grace = 0;
    leavingRef.current = false;
    // A late profile used to rebuild trackRemote, rerun this effect, and
    // disconnect() latched _leaving. The next connect then threw hangup and
    // the room stayed on Joining. Identity updates go through applyIdentity.
    client.beginJoin();
    hushRoster();
    rosterRef.current.clear();
    client.setCallbacks({
      onLocalStream: (s) => alive && setLocal(s),
      onCameraStream: (s) => {
        if (!alive) return;
        setCamera(s);
        setCamOn(!!s);
        setBlur(s ? client.cameraBlurState() : { controllable: false, known: false, on: null });
        if (s) setStageId("local");
      },
      onCameraBlur: (state) => {
        if (!alive) return;
        setBlur(state?.known || state?.controllable ? state : { controllable: false, known: false, on: null });
      },
      onScreenStream: (s) => {
        if (!alive) return;
        setScreen(s);
        setSharing(!!s);
        if (s) setStageId("local");
      },
      onRemoteAdded: (s) => {
        if (!alive) return;
        trackRemote(s, true);
        setRemote((prev) => new Map(prev).set(s.getID(), s));
        if (isScreen(s)) setStageId(speakerKey(s, false, usernameRef.current));
      },
      onRemoteSubscribed: (s) => {
        if (!alive) return;
        setRemote((prev) => new Map(prev).set(s.getID(), s));
        if (isScreen(s)) setStageId(speakerKey(s, false, usernameRef.current));
      },
      onRemoteRemoved: (s) => {
        if (!alive) return;
        trackRemote(s, false);
        setRemote((prev) => {
          const next = new Map(prev);
          next.delete(s.getID());
          return next;
        });
      },
      onRemoteCleared: () => {
        if (!alive) return;
        hushRoster();
        rosterRef.current.clear();
        setRemote(new Map());
        setPeerMedia(new Map());
        setCamera(null);
        setScreen(null);
        setCamOn(false);
        setSharing(false);
      },
      onChat: (msg) => {
        if (!alive) return;
        setMessages((prev) => [...prev, msg]);
        setChatOpen((open) => {
          if (!open) {
            setUnread((n) => n + 1);
            setToast(msg);
          }
          return open;
        });
      },
      onPeerMedia: (info) => {
        if (!alive || !info?.userid) return;
        setPeerMedia((prev) => {
          const cur = prev.get(info.userid);
          if (
            cur &&
            cur.mute === info.mute &&
            cur.mic === info.mic &&
            cur.cam === info.cam &&
            cur.share === info.share
          ) {
            return prev;
          }
          const next = new Map(prev);
          next.set(info.userid, {
            mute: !!info.mute,
            mic: info.mic !== false,
            cam: !!info.cam,
            share: !!info.share,
          });
          return next;
        });
      },
      onMicDevices: (list) => {
        if (!alive) return;
        setMics(list);
        const cur = client.currentMic();
        setMicId(cur.deviceId);
        setMicLabel(cur.label);
        setMicLive(cur.live);
      },
      onDevices: (info) => {
        if (!alive) return;
        setMics(info.mics || []);
        setAudioSinks(info.speakers || []);
        const cur = client.currentMic();
        setMicId(cur.deviceId);
        setMicLabel(cur.label);
        setMicLive(cur.live);
        const spk = client.currentSpeaker();
        setSinkId(spk.deviceId);
        setSinkLabel(spk.label);
        const lostCurrentMic = (info.removedMics || []).some(
          (m) =>
            (cur.deviceId && m.deviceId === cur.deviceId) ||
            (cur.label && m.label === cur.label)
        );
        const lostCurrentSpk = (info.removedSpeakers || []).some(
          (s) => spk.deviceId && s.deviceId === spk.deviceId
        );
        if (info.addedMics?.length) {
          setToast({
            who: "Microphone",
            text: `Connected ${firstDeviceLabel(info.addedMics, "microphone")}`,
            kind: "system",
          });
        } else if (info.removedMics?.length && !lostCurrentMic) {
          setToast({
            who: "Microphone",
            text: `${firstDeviceLabel(info.removedMics, "Microphone")} disconnected`,
            kind: "system",
          });
        } else if (info.addedSpeakers?.length) {
          setToast({
            who: "Speaker",
            text: `Connected ${firstDeviceLabel(info.addedSpeakers, "speaker")}`,
            kind: "system",
          });
        } else if (info.removedSpeakers?.length && !lostCurrentSpk) {
          setToast({
            who: "Speaker",
            text: `${firstDeviceLabel(info.removedSpeakers, "Speaker")} disconnected`,
            kind: "system",
          });
        }
      },
      onSpeakerLost: (info) => {
        if (!alive) return;
        setSinkId("");
        setSinkLabel("System default");
        setToast({
          who: "Speaker",
          text: `${info?.label || "Speaker"} disconnected. Using system default.`,
          kind: "system",
        });
      },
      onMicLost: (info) => {
        if (!alive) return;
        setMicLive(false);
        const name = info?.label ? `${info.label} disconnected` : "Microphone disconnected";
        setToast({ who: "Microphone", text: name, kind: "system" });
        client
          .listAudioDevices()
          .then(({ mics, speakers }) => {
            if (!alive) return;
            setMics(mics);
            setAudioSinks(speakers);
            if (!mics.length) setMicOpen(true);
          })
          .catch(() => undefined);
      },
      onOneWay: (reason) => {
        if (!alive || leavingRef.current || recoveringRef.current) return;
        const at = Date.now();
        reportLink(apiBase, roomId, "recover", reason || "one-way-audio");
        noteChat(`No inbound audio — reconnecting ${formatClock(at)}`);
        setToast({
          who: "Meeting",
          text: `No inbound audio — reconnecting ${formatClock(at)}`,
          kind: "system",
        });
        recoverRef.current();
      },
      onStreamRepair: (info) => {
        if (!alive || leavingRef.current || recoveringRef.current) return;
        const at = Date.now();
        const name = info?.name || "participant";
        reportLink(apiBase, roomId, "recover", `resubscribe:${info?.reason || "failed-stream"}`);
        noteChat(`Restoring audio from ${name} ${formatClock(at)}`);
        setToast({
          who: "Meeting",
          text: `Restoring audio from ${name} ${formatClock(at)}`,
          kind: "system",
        });
      },
      onDiagnostics: (report) => {
        if (!(report.ended || (alive && !leavingRef.current))) return undefined;
        return reportMedia(apiBase, roomId, report).then((ids) => {
          if (!alive || leavingRef.current || recoveringRef.current || report.ended) return;
          for (const id of ids) client.repairRemoteById(id);
        });
      },
      onDisconnected: (msg) => {
        if (!alive || leavingRef.current || recoveringRef.current) return;
        hushRoster();
        const at = Date.now();
        reportLink(apiBase, roomId, "drop", msg || "room-disconnected");
        noteChat(`Dropped ${formatClock(at)}${msg ? ` — ${msg}` : ""}`);
        setReconnectAt(at);
        setToast({ who: "Meeting", text: `Dropped ${formatClock(at)}`, kind: "system" });
        if (grace) return;
        const wait = Math.max(4000, 10000 - (Date.now() - lastRecoverOkRef.current));
        grace = window.setTimeout(() => {
          grace = 0;
          if (!alive || leavingRef.current || recoveringRef.current || client.connected) return;
          recoverRef.current();
        }, wait);
      },
      onReconnecting: () => {
        if (!alive || leavingRef.current) return;
        hushRoster();
        const at = Date.now();
        setReconnectAt(at);
        setReconnecting(true);
        setToast({ who: "Meeting", text: `Reconnecting… ${formatClock(at)}`, kind: "system" });
      },
      onSocketReconnected: () => {
        if (!alive || leavingRef.current || recoveringRef.current) return;
        if (grace) {
          window.clearTimeout(grace);
          grace = 0;
        }
        const at = Date.now();
        reportLink(apiBase, roomId, "socket-ok", "erizo-ws");
        hushRoster();
        setReconnecting(false);
        noteChat(`Socket reconnected ${formatClock(at)}`);
        setToast({ who: "Meeting", text: `Reconnected ${formatClock(at)}`, kind: "system" });
      },
      onMicChanged: (info) => {
        if (!alive) return;
        setMicId(info?.deviceId || "");
        setMicLabel(info?.label || "");
        setMicLive(!!info?.live);
        if (info?.reason === "fallback") {
          setToast({
            who: "Microphone",
            text: `Switched to ${info.label || "another microphone"}`,
            kind: "system",
          });
        } else if (info?.reason === "recovered") {
          setToast({
            who: "Microphone",
            text: `Reconnected ${info.label || "microphone"}`,
            kind: "system",
          });
          setMicOpen(false);
        }
      },
    });
    const startCall = () => {
      if (!alive || leavingRef.current) return;
      unlockAudio();
      client.recordingEnabled = false;
      fetchRecordings(apiBase)
        .then((data) => {
          client.recordingEnabled = data.enabled === true;
        })
        .catch(() => {
          client.recordingEnabled = false;
        })
        .finally(() => {
          if (!alive || leavingRef.current) return;
          client
            .connect(erizoSrc, {
              audio: true,
              video: false,
              role: "presenter",
              identity: identityRef.current,
              deferMedia: needTap,
              skipMic,
              requireMic,
            })
            .then(() => {
              if (!alive) return;
              unlockAudio();
              client.kickPlayback();
              setLoading(false);
              noteChat("You joined");
            })
            .catch((err) => {
              if (!alive || leavingRef.current || recoveringRef.current) return;
              if (err instanceof Error && err.message === "hangup") return;
              recoverRef.current();
            });
        });
    };
    if (client.recordingContext?.meetingSessionId) startCall();
    else {
      openMeetingSession(apiBase, roomId).then((presence) => {
        if (!alive || leavingRef.current) return;
        if (presence) {
          client.recordingContext = {
            ...client.recordingContext,
            meetingSessionId: presence.meetingSessionId,
            participantSessionId: presence.participantSessionId,
            connectionId: presence.connectionId,
          };
        }
        startCall();
      }).catch(() => startCall());
    }
    return () => {
      alive = false;
      if (grace) window.clearTimeout(grace);
      leavingRef.current = true;
      client.disconnect();
    };
  }, [client, erizoSrc, needTap, skipMic, requireMic, apiBase, roomId, noteChat, hushRoster, trackRemote]);

  const speakers = useMemo(() => {
    const tiles = groupSpeakers(local, camera, screen, remote, username, identity);
    return tiles.map((t) => {
      if (t.local) {
        return { ...t, micOff: muted || !micLive };
      }
      const id = t.person?.userid || t.person?.userId;
      const extra = id ? peerProfilesRef.current.get(id) : null;
      const media = id ? peerMedia.get(id) : null;
      const micOff = !!media && (media.mute || media.mic === false);
      if (!extra) return { ...t, micOff };
      return {
        ...t,
        micOff,
        person: {
          ...t.person,
          avatarUrl: t.person.avatarUrl || extra.avatarUrl || "",
          punkId: parsePunkId(t.person.punkId) ?? extra.punkId ?? null,
          address: t.person.address || extra.address || "",
          name: hasRealName(t.person) ? t.person.name : extra.name || t.person.name,
        },
      };
    });
  }, [local, camera, screen, remote, username, identity, peerTick, peerMedia, muted, micLive]);

  useEffect(() => {
    const ids = new Set();
    remote.forEach((stream) => {
      const a = stream?.getAttributes?.() || {};
      const id = String(a.userid || a.userId || a.id || "").trim();
      if (id) ids.add(id);
    });
    let cancelled = false;
    (async () => {
      let changed = false;
      for (const id of ids) {
        if (peerProfilesRef.current.has(id)) continue;
        try {
          const p = await fetchProfile(apiBase, id);
          if (cancelled) return;
          peerProfilesRef.current.set(id, p);
          changed = true;
        } catch {
          if (cancelled) return;
          peerProfilesRef.current.set(id, null);
          changed = true;
        }
      }
      if (changed) setPeerTick((n) => n + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [remote, apiBase]);
  const people = useMemo(() => new Set(speakers.map((s) => s.speakerId)).size, [speakers]);

  useEffect(() => {
    if (!friendOf) return;
    if (!speakers.some((s) => s.speakerId === friendOf.speakerId)) {
      setFriendOf(null);
      setFriendHint("");
    }
  }, [speakers, friendOf]);

  const placeFriend = useCallback(() => {
    if (!friendOf) {
      setFriendPos(null);
      return;
    }
    const el = railRef.current?.querySelector(`[data-slot="${CSS.escape(friendOf.id)}"]`);
    if (!el) {
      setFriendPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setFriendPos({
      right: Math.max(8, window.innerWidth - r.right),
      bottom: Math.max(8, window.innerHeight - r.top + 8),
    });
  }, [friendOf]);

  useLayoutEffect(() => {
    placeFriend();
    const rail = railRef.current;
    window.addEventListener("resize", placeFriend);
    rail?.addEventListener("scroll", placeFriend);
    return () => {
      window.removeEventListener("resize", placeFriend);
      rail?.removeEventListener("scroll", placeFriend);
    };
  }, [placeFriend, speakers]);

  const stage = useMemo(() => {
    if (stageId) {
      const found = speakers.find((e) => e.id === stageId || e.streamIds.includes(stageId));
      if (found) return found;
    }
    return speakers.find((e) => isScreen(e.stream)) || speakers.find((e) => streamHasVideo(e.stream)) || speakers[0] || null;
  }, [speakers, stageId]);

  const hangup = useCallback(() => {
    leavingRef.current = true;
    recoveringRef.current = true;
    closeMeetingSession(apiBase, roomId, client.recordingContext, "exit");
    client.disconnect();
    onExit();
  }, [apiBase, client, onExit, roomId]);

  useEffect(() => {
    const gone = () => {
      closeMeetingSession(apiBase, roomId, client.recordingContext, "pagehide");
      leaveRoom(apiBase, roomId);
    };
    window.addEventListener("pagehide", gone);
    return () => window.removeEventListener("pagehide", gone);
  }, [apiBase, roomId]);

  useEffect(() => {
    if (loading) return undefined;
    let misses = 0;
    const t = setInterval(() => {
      if (leavingRef.current || recoveringRef.current || client.connected) return;
      fetchPresence(apiBase, roomId)
        .then((p) => {
          if (p.unknown || p.inRoom) {
            misses = 0;
            return;
          }
          misses += 1;
          if (misses >= 3) recoverRef.current();
        })
        .catch(() => undefined);
    }, 20000);
    return () => clearInterval(t);
  }, [loading, apiBase, roomId, client]);

  const openPerson = (entry) => {
    setStageId(entry.id);
    if (entry.local) {
      setFriendOf(null);
      setFriendHint("");
      return;
    }
    setFriendOf(entry);
    setFriendHint("");
  };

  const sendFriendRequest = async (person) => {
    const theirId = person.userid || person.userId;
    if (theirId && username && theirId === username) {
      setFriendHint("That's you.");
      return;
    }
    const native = inApp || isNativeMeetHost();
    if (!native) {
      let uiOrigin;
      try {
        uiOrigin = pickAddOrigin(identity.via, username);
      } catch (err) {
        setFriendHint(err instanceof Error ? err.message : "Sign in with Beagle again.");
        return;
      }
      const win = window.open("about:blank", "beagle-connect", "width=420,height=560");
      if (!win) {
        setFriendHint("Popup blocked — allow popups and retry");
        return;
      }
      setAdding(true);
      setFriendHint("");
      try {
        let address = String(person.address || "").trim();
        if (!isCarrierAddress(address) && theirId) {
          const profile = await fetchProfile(apiBase, theirId);
          address = String(profile?.address || "").trim();
        }
        if (!isCarrierAddress(address)) {
          try {
            win.close();
          } catch {
            /* ignore */
          }
          setFriendHint("This person has no Beagle address yet — they need to sign in here first.");
          return;
        }
        await addBeagleFriend({ address, name: person.name, win, uiOrigin, via: identity.via, userid: username });
        setFriendOf(null);
        setToast({ who: "Beagle", text: `Friend request sent to ${person.name || "them"}`, kind: "system" });
      } catch (err) {
        setFriendHint(err instanceof Error ? err.message : "Could not send friend request");
      } finally {
        setAdding(false);
      }
      return;
    }

    setAdding(true);
    setFriendHint("");
    try {
      let address = String(person.address || "").trim();
      let name = person.name || theirId || "";
      if (theirId) {
        const profile = await fetchProfile(apiBase, theirId);
        address = String(profile?.address || address).trim();
        if (profile?.name) name = profile.name;
      }
      if (!isCarrierAddress(address)) {
        setFriendHint("This person has no Beagle address yet — they need to sign in here first.");
        return;
      }
      postToNativeHost({
        type: "beagle-meet-add-friend",
        carrierAddress: address,
        address,
        name,
      });
      setFriendOf(null);
      setToast({ who: "Beagle", text: `Friend request sent to ${name || "them"}`, kind: "system" });
    } catch (err) {
      setFriendHint(err instanceof Error ? err.message : "Could not send friend request");
    } finally {
      setAdding(false);
    }
  };

  const send = () => {
    const text = chat.trim();
    if (!text) return;
    if (!client.sendChat(text)) return;
    setMessages((prev) => [
      ...prev,
      {
        who: identity.name || "You",
        text,
        ts: Date.now(),
        userid: username,
        avatarUrl: identity.avatarUrl,
        punkId: identity.punkId,
      },
    ]);
    setChat("");
  };

  if (loading) {
    const tap = () => {
      unlockAudio();
      client.captureMic();
    };
    return (
      <div className="bm-status" onPointerDown={tap} onClick={tap} role="button" tabIndex={0}>
        <div className="bm-spin" />
        <div>Joining {roomName || "room"}…</div>
        {needTap && !micLabel ? <div className="bm-status-hint">Tap to enable microphone</div> : null}
        {reconnecting ? (
          <div className="bm-status-hint">Reconnecting… {formatClock(reconnectAt || Date.now())}</div>
        ) : null}
        {micLabel ? <div className="bm-status-hint">Mic: {micLabel}</div> : null}
        {canSink && (audioSinks.length || sinkId) ? (
          <div className="bm-status-hint">Speaker: {sinkLabel}</div>
        ) : null}
      </div>
    );
  }
  if (error) {
    return (
      <div className="bm-status">
        <p>{error}</p>
        <button type="button" className="bm-retry" onClick={onExit}>
          Back
        </button>
      </div>
    );
  }

  const speaking = people;

  return (
    <div className="bm-call">
      <div className="bm-top">
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <button type="button" className="bm-back" onClick={hangup} aria-label="Back">
            ‹
          </button>
          <div style={{ minWidth: 0 }}>
            <div className="bm-rname">{roomName || "Meeting"}</div>
            <div className="bm-rid">{roomId}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="bm-chip2">
            <span className="bm-dot" />
            {formatElapsed(now - startedAt)}
          </div>
          <div className="bm-chip2">{speaking} in room</div>
        </div>
      </div>
      {reconnecting ? (
        <div className="bm-reconnect" aria-live="polite">
          Reconnecting… {formatClock(reconnectAt || Date.now())}
        </div>
      ) : null}

      <div
        className="bm-body"
        onClick={(e) => {
          if (e.target.closest(".bm-pill, .bm-chat, .bm-toast, .bm-rail, .bm-pip, .bm-reveal, .bm-friend, .bm-micmenu")) return;
          setMicOpen(false);
          setEffectsOpen(false);
          setShowControls((v) => !v);
        }}
      >
        <div className="bm-stage">
          {stage ? (
            <VideoTile stream={stage.stream} local={stage.local} localIdentity={identity} person={stage.person} variant="stage" micOff={stage.micOff} audioStream={stage.audioStream} />
          ) : (
            <div>Waiting for participants</div>
          )}
          <div className="bm-hd">HD</div>
          <div className="bm-pip" onClick={(e) => e.stopPropagation()}>
            {camera ? (
              <VideoTile stream={camera} local localIdentity={identity} variant="pip" micOff={muted || !micLive} audioStream={local} />
            ) : (
              <div className="bm-pip-off">
                {muted || !micLive ? (
                  <span className="bm-mutechip" title="Microphone off" aria-label="Microphone off">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M6 11a6 6 0 0 0 12 0M12 17v3M8 21h8M5 5l14 14" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                  </span>
                ) : null}
                <MeetAvatar identity={identity} size={46} />
                <span className="bm-you">You · cam off</span>
              </div>
            )}
          </div>
        </div>

        <div className="bm-rail" ref={railRef} onClick={(e) => e.stopPropagation()}>
          {speakers.map((e) => (
            <div key={e.id} className="bm-slot" data-slot={e.id}>
              <button
                type="button"
                className={`bm-tile ${stage?.id === e.id ? "active" : ""}`}
                onClick={() => openPerson(e)}
              >
                {stage?.id === e.id ? <span className="bm-active">ACTIVE</span> : null}
                <VideoTile stream={e.stream} local={e.local} localIdentity={identity} person={e.person} variant="thumb" micOff={e.micOff} audioStream={e.audioStream} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {showControls ? (
        <div className="bm-float">
          <div
            className="bm-pill"
            onClick={(e) => {
              if (!e.target.closest(".bm-micwrap")) setMicOpen(false);
              if (!e.target.closest(".bm-fxwrap")) setEffectsOpen(false);
            }}
          >
            <div className="bm-micwrap">
              <button
                type="button"
                className={`bm-ctrl ${muted || !micLive ? "off" : ""}`}
                onClick={async () => {
                  unlockAudio();
                  if (!client.hasLocalAudio()) {
                    try {
                      await client.enableMic();
                      setMuted(false);
                      setMicLive(client.hasLocalAudio());
                      setMicId(client.currentMic().deviceId);
                      setMicLabel(client.currentMic().label);
                    } catch (err) {
                      setToast({
                        who: "Microphone",
                        text: err instanceof Error ? err.message : "Microphone permission denied",
                        kind: "system",
                      });
                    }
                    return;
                  }
                  const next = !muted;
                  client.muteAudio(next);
                  setMuted(next);
                }}
              >
                <Glyph kind="mic" off={muted || !micLive} />
                <span>MIC</span>
              </button>
              <button
                type="button"
                className="bm-micpick"
                aria-label="Choose microphone or speaker"
                aria-expanded={micOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  unlockAudio();
                  setMicOpen((v) => !v);
                  client
                    .listAudioDevices()
                    .then(({ mics: list, speakers: outs }) => {
                      setMics(list);
                      setAudioSinks(outs);
                      const cur = client.currentMic();
                      setMicId(cur.deviceId);
                      setMicLabel(cur.label);
                      setMicLive(cur.live);
                      const spk = client.currentSpeaker();
                      setSinkId(spk.deviceId);
                      setSinkLabel(spk.label);
                    })
                    .catch(() => undefined);
                }}
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M3 4.5L6 8l3-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {micOpen ? (
                <div className="bm-micmenu" onClick={(e) => e.stopPropagation()}>
                  <div className="bm-micmenu-h">Microphone</div>
                  {mics.length === 0 ? (
                    <div className="bm-micempty">
                      No microphone found. Connect a device, then choose it here without leaving the call.
                      <button
                        type="button"
                        className="bm-micopt"
                        style={{ marginTop: 8 }}
                        disabled={micBusy}
                        onClick={async () => {
                          setMicBusy(true);
                          try {
                            const { mics: list, speakers: outs } = await client.listAudioDevices();
                            setMics(list);
                            setAudioSinks(outs);
                            if (list[0]) {
                              await client.switchMic(list[0].deviceId, { preferred: true, reason: "switch" });
                              const cur = client.currentMic();
                              setMicId(cur.deviceId);
                              setMicLabel(cur.label);
                              setMicLive(cur.live);
                              setMuted(false);
                              setMicOpen(false);
                            }
                          } catch (err) {
                            setToast({
                              who: "Microphone",
                              text: err instanceof Error ? err.message : "Could not open microphone",
                              kind: "system",
                            });
                          } finally {
                            setMicBusy(false);
                          }
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    mics.map((d) => (
                      <button
                        key={d.deviceId}
                        type="button"
                        className={`bm-micopt ${d.deviceId === micId || (micLabel && d.label === micLabel) ? "on" : ""}`}
                        disabled={micBusy}
                        onClick={async () => {
                          setMicBusy(true);
                          const wasDead = !client.hasLocalAudio();
                          try {
                            await client.switchMic(d.deviceId, { preferred: true, reason: "switch" });
                            const cur = client.currentMic();
                            setMicId(cur.deviceId);
                            setMicLabel(cur.label);
                            setMicLive(cur.live);
                            if (wasDead) {
                              client.muteAudio(false);
                              setMuted(false);
                            }
                            setMicOpen(false);
                          } catch (err) {
                            setToast({
                              who: "Microphone",
                              text: err instanceof Error ? err.message : "Could not switch microphone",
                              kind: "system",
                            });
                          } finally {
                            setMicBusy(false);
                          }
                        }}
                      >
                        {d.label}
                      </button>
                    ))
                  )}
                  {canSink ? (
                    <>
                      <div className="bm-micmenu-h bm-micmenu-spk">Speaker</div>
                      <button
                        type="button"
                        className={`bm-micopt ${!sinkId ? "on" : ""}`}
                        disabled={micBusy}
                        onClick={async () => {
                          setMicBusy(true);
                          try {
                            await client.setSpeaker("");
                            const spk = client.currentSpeaker();
                            setSinkId(spk.deviceId);
                            setSinkLabel(spk.label);
                            setMicOpen(false);
                          } finally {
                            setMicBusy(false);
                          }
                        }}
                      >
                        System default
                      </button>
                      {audioSinks.map((d) => (
                        <button
                          key={d.deviceId}
                          type="button"
                          className={`bm-micopt ${d.deviceId === sinkId ? "on" : ""}`}
                          disabled={micBusy}
                          onClick={async () => {
                            setMicBusy(true);
                            try {
                              await client.setSpeaker(d.deviceId);
                              const spk = client.currentSpeaker();
                              setSinkId(spk.deviceId);
                              setSinkLabel(spk.label);
                              setMicOpen(false);
                            } catch (err) {
                              setToast({
                                who: "Speaker",
                                text: err instanceof Error ? err.message : "Could not switch speaker",
                                kind: "system",
                              });
                            } finally {
                              setMicBusy(false);
                            }
                          }}
                        >
                          {d.label}
                        </button>
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className={`bm-ctrl ${camOn ? "" : "off"}`}
              disabled={camBusy || reconnecting}
              aria-label={camOn ? "Turn camera off" : "Turn camera on"}
              onClick={async () => {
                unlockAudio();
                setCamBusy(true);
                try {
                  if (camOn) await client.setVideo(false);
                  else await client.setVideo(true);
                } catch (err) {
                  setToast({
                    who: "Camera",
                    text: err instanceof Error ? err.message : "Camera failed",
                  });
                } finally {
                  setCamBusy(false);
                }
              }}
            >
              <Glyph kind="cam" off={!camOn} />
              <span>{camOn ? "CAM" : "CAM OFF"}</span>
            </button>
            {showEffects ? <div className="bm-fxwrap">
              <button
                type="button"
                className="bm-ctrl"
                aria-expanded={effectsOpen}
                aria-pressed={effectsOpen}
                aria-label="Background effects"
                title="Background effects"
                onClick={() => setEffectsOpen((open) => !open)}
              >
                <Glyph kind="effects" />
                <span>EFFECTS</span>
              </button>
              {effectsOpen ? (
                <div className="bm-fxmenu" onClick={(e) => e.stopPropagation()}>
                  <div className="bm-micmenu-h">Effects</div>
                  {blur.controllable ? (
                    <button
                      type="button"
                      className={`bm-micopt ${blur.on ? "on" : ""}`}
                      disabled={camBusy || reconnecting}
                      aria-pressed={!!blur.on}
                      onClick={async () => {
                        setCamBusy(true);
                        try {
                          await client.setCameraBlur(!blur.on);
                          setBlur(client.cameraBlurState());
                        } catch (err) {
                          setToast({
                            who: "Camera",
                            text: err instanceof Error ? err.message : "Background blur failed",
                          });
                        } finally {
                          setCamBusy(false);
                        }
                      }}
                    >
                      {blur.on ? "Background blur on" : "Background blur off"}
                    </button>
                  ) : blur.known ? (
                    <p className="bm-fxnote">
                      Background blur is {blur.on ? "on" : "off"}.
                      {desktopMac ? "" : " Your device manages this effect."}
                    </p>
                  ) : null}
                  {desktopMac ? (
                    <>
                      <p className="bm-fxtitle">{MAC_EFFECTS_HELP.title}</p>
                      <ol className="bm-fxsteps">
                        {MAC_EFFECTS_HELP.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                      <p className="bm-fxnote">{MAC_EFFECTS_HELP.note}</p>
                      <p className="bm-fxnote">{MAC_EFFECTS_HELP.ventura}</p>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div> : null}
            {camOn && mobileCamera ? (
              <button
                type="button"
                className="bm-ctrl"
                disabled={camBusy || reconnecting}
                aria-label="Switch front and back camera"
                title="Switch front and back camera"
                onClick={async () => {
                  setCamBusy(true);
                  try {
                    await client.switchCamera();
                  } catch (err) {
                    setToast({ who: "Camera", text: err instanceof Error ? err.message : "Camera switch failed" });
                  } finally {
                    setCamBusy(false);
                  }
                }}
              >
                <Glyph kind="flip" /><span>FLIP</span>
              </button>
            ) : null}
            {showScreenShare ? (
            <button
              type="button"
              className="bm-ctrl"
              disabled={shareBusy}
              onClick={async () => {
                setShareBusy(true);
                try {
                  if (sharing) client.stopScreenShare();
                  else await client.startScreenShare();
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Screen share failed");
                } finally {
                  setShareBusy(false);
                }
              }}
            >
              <Glyph kind="screen" />
              <span>SCREEN</span>
            </button>
            ) : null}
            <button
              type="button"
              className="bm-ctrl"
              style={{ position: "relative" }}
              onClick={() => {
                setChatOpen((v) => !v);
                setUnread(0);
                setToast(null);
              }}
            >
              <Glyph kind="chat" />
              <span>CHAT</span>
              {unread > 0 ? <i className="bm-unread">{unread}</i> : null}
            </button>
            <div className="bm-div" />
            <button type="button" className="bm-leave" onClick={hangup}>
              EXIT
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="bm-reveal" onClick={() => setShowControls(true)}>
          CLICK TO SHOW CONTROLS
        </button>
      )}

      {chatOpen ? (
        <div className="bm-chat" onClick={(e) => e.stopPropagation()}>
          <div className="bm-chat-h">
            Messages
            <button type="button" className="bm-x" onClick={() => setChatOpen(false)}>
              ×
            </button>
          </div>
          <div className="bm-msgs">
            {messages.map((m, i) => (
              <div key={`${m.ts}-${i}`} className="bm-msg">
                <MeetAvatar
                  identity={{
                    name: m.who,
                    userid: m.userid,
                    userId: m.userid,
                    avatarUrl: m.avatarUrl,
                    punkId: m.punkId,
                  }}
                  size={28}
                />
                <div className="bm-msg-body">
                  <div className="bm-who">
                    {m.who}
                    {m.ts ? <span className="bm-when"> {formatClock(m.ts)}</span> : null}
                  </div>
                  <div className="bm-txt">
                    <ChatText text={m.text} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <form
            className="bm-comp"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input value={chat} onChange={(e) => setChat(e.target.value)} placeholder="Message the room…" />
            <button type="submit" className="bm-send">
              Send
            </button>
          </form>
        </div>
      ) : toast ? (
        <button
          type="button"
          className="bm-toast"
          onClick={() => {
            if (toast.kind === "system") {
              setToast(null);
              return;
            }
            setChatOpen(true);
            setUnread(0);
            setToast(null);
          }}
        >
          <div className="bm-toast-row">
            <MeetAvatar
              identity={{
                name: toast.who,
                userid: toast.userid,
                userId: toast.userid,
                avatarUrl: toast.avatarUrl,
                punkId: toast.punkId,
              }}
              size={28}
            />
            <div>
              <div className="bm-who">{toast.who}</div>
              <div className="bm-txt">{toast.text}</div>
            </div>
          </div>
        </button>
      ) : null}

      {friendOf && !friendOf.local && friendPos ? (
        <div
          className="bm-friend"
          style={{ right: friendPos.right, bottom: friendPos.bottom }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bm-friend-h">
            <MeetAvatar identity={friendOf.person} size={40} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="bm-friend-name">{friendOf.person?.name || "Participant"}</div>
              <div className="bm-friend-sub">{shortId(friendOf.person?.userid || friendOf.person?.userId)}</div>
            </div>
            <button
              type="button"
              className="bm-x"
              onClick={() => {
                setFriendOf(null);
                setFriendHint("");
              }}
            >
              ×
            </button>
          </div>
          <button
            type="button"
            className="bm-friend-go"
            disabled={adding}
            onClick={() => sendFriendRequest(friendOf.person)}
          >
            {adding ? "Opening Beagle…" : "Add as Beagle friend"}
          </button>
          {isOwner ? (
            <button
              type="button"
              className="bm-friend-go"
              style={{ marginTop: 8, background: "transparent", border: "1px solid oklch(0.52 0.18 25)", color: "oklch(0.78 0.08 25)" }}
              disabled={adding}
              onClick={async () => {
                const id = friendOf.person?.userid || friendOf.person?.userId;
                if (!id) return;
                setAdding(true);
                setFriendHint("");
                try {
                  await blockUser(apiBase, roomId, id);
                  setFriendHint("Blocked — they cannot rejoin this room.");
                } catch (err) {
                  setFriendHint(err instanceof Error ? err.message : "Block failed");
                } finally {
                  setAdding(false);
                }
              }}
            >
              Block from this room
            </button>
          ) : null}
          {friendHint ? <div className="bm-friend-hint">{friendHint}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
